import { Fragment, useEffect, useRef, useState } from 'react'
import { BRAND_NAME } from './BrandLogo'
import TscheppaHeroVideo from './TscheppaHeroVideo'
import { CATALOG, thumbnailUrl, type CatalogSpecies } from '../data/catalog'
import { ROUTES, estimateTime } from '../data/routes'
import { useStories } from '@/hooks/useStories'
import { useAuth } from '../auth'
import SiteFooter from './SiteFooter'
import { useT } from '../i18n'
import { useTheme } from '../theme'
import { StoryCard } from './StoryCard'
import { VerlaufRegistrationDialog, SurveyDialog } from './dialog/presets'
import '../frog-view.css'

// Featured species — random mix of Fauna & Flora on each page load
function pickFeatured(count = 6): CatalogSpecies[] {
  const fauna = CATALOG.filter(s => s.group === 'Fauna')
  const flora = CATALOG.filter(s => s.group === 'Flora')
  const shuffle = <T,>(arr: T[]): T[] => {
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]]
    }
    return a
  }
  const half = Math.ceil(count / 2)
  const pickedFauna = shuffle(fauna).slice(0, half)
  const pickedFlora = shuffle(flora).slice(0, count - half)
  const interleaved: CatalogSpecies[] = []
  for (let i = 0; i < half; i++) {
    if (pickedFauna[i]) interleaved.push(pickedFauna[i])
    if (pickedFlora[i]) interleaved.push(pickedFlora[i])
  }
  return interleaved
}

export default function LandingPage() {
  const { user, startGoogleLogin } = useAuth()
  const t = useT()
  const { skyMode, showPhoto, overlayStyle } = useTheme()
  const { stories: STORIES } = useStories()
  const speciesCount = CATALOG.length
  const faunaCount = CATALOG.filter(s => s.group === 'Fauna').length
  const floraCount = CATALOG.filter(s => s.group === 'Flora').length
  const [showRegister, setShowRegister] = useState(false)
  const [showSurvey, setShowSurvey] = useState(false)
  const surveyChecked = useRef(false)
  // Email-only registration: custom event from VerlaufRegistrationDialog
  useEffect(() => {
    const onSurveyReady = () => {
      if (!surveyChecked.current && localStorage.getItem('tschepp-survey-pending')) {
        surveyChecked.current = true
        localStorage.removeItem('tschepp-survey-pending')
        setShowSurvey(true)
      }
    }
    window.addEventListener('tschepp-survey-ready', onSurveyReady)
    return () => window.removeEventListener('tschepp-survey-ready', onSurveyReady)
  }, [])
  const [featured] = useState(() => pickFeatured(6))

  return (
    <div className={`landing fv-page fv-page--${skyMode}${showPhoto ? ' sg-bg-photo' : ''}`}>
      {showPhoto && <div className="sg-bg-photo__img"><div className="sg-bg-photo__overlay" style={overlayStyle} /></div>}

      {/* ── Hero ── */}
      <section className="landing-hero">
        <TscheppaHeroVideo className="landing-hero__video" paused={showRegister} />
        <div className="landing-hero__overlay" />
        <div className="landing-hero__content">
          <div className="landing-hero__brand">
            <img src="/logo-tscheppa-weiss.svg" alt="Tscheppaschlucht" className="landing-hero__logo" />
            <span className="landing-hero__brand-name">{BRAND_NAME}</span>
          </div>
          <h1 className="landing-hero__title">
            {t.landing.hero.title.split('\n').map((l, i) => <Fragment key={i}>{i > 0 && <br />}{l}</Fragment>)}
          </h1>
          <p className="landing-hero__subtitle">
            {t.landing.hero.subtitle(speciesCount)}
          </p>
          <div className="landing-hero__actions">
            {!user ? (
              <button className="landing-hero__cta" onClick={() => setShowRegister(true)}>
                {t.landing.hero.cta}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <a href="/browse" className="landing-hero__cta">
                {t.landing.hero.secondaryCta}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            )}
            {!user && (
              <a href="/browse" className="landing-hero__secondary">
                {t.landing.hero.secondaryCta}
              </a>
            )}
          </div>
          <div className="landing-hero__scroll-hint">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </div>
        </div>
      </section>

      {/* ── Intro ── */}
      <section className="landing-intro">
        <div className="landing-intro__inner">
          <h2 className="landing-intro__heading">{t.landing.intro.heading.split('\n').map((l, i) => <Fragment key={i}>{i > 0 && <br />}{l}</Fragment>)}</h2>
          <p className="landing-intro__text">
            {t.landing.intro.text(speciesCount)}
          </p>
          <div className="landing-intro__stats">
            <div className="landing-intro__stat">
              <span className="landing-intro__stat-num">{faunaCount}</span>
              <span className="landing-intro__stat-label">{t.landing.stats.animals}</span>
            </div>
            <div className="landing-intro__stat">
              <span className="landing-intro__stat-num">{floraCount}</span>
              <span className="landing-intro__stat-label">{t.landing.stats.plants}</span>
            </div>
            <div className="landing-intro__stat">
              <span className="landing-intro__stat-num">AR</span>
              <span className="landing-intro__stat-label">{t.landing.stats.ar}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Community ── */}
      {!user && (
        <section className="landing-community">
          <div className="landing-community__inner">
            <h2 className="landing-community__heading">{t.landing.community.heading}</h2>
            <p className="landing-community__text">
              {t.landing.community.text}
            </p>
            <div className="landing-community__features">
              <div className="landing-community__feature">
                <div className="landing-community__feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                  </svg>
                </div>
                <span className="landing-community__feature-title">{t.landing.community.features.like.title}</span>
                <span className="landing-community__feature-desc">{t.landing.community.features.like.description}</span>
              </div>
              <div className="landing-community__feature">
                <div className="landing-community__feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                </div>
                <span className="landing-community__feature-title">{t.landing.community.features.comment.title}</span>
                <span className="landing-community__feature-desc">{t.landing.community.features.comment.description}</span>
              </div>
              <div className="landing-community__feature">
                <div className="landing-community__feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </div>
                <span className="landing-community__feature-title">{t.landing.community.features.ar.title}</span>
                <span className="landing-community__feature-desc">{t.landing.community.features.ar.description}</span>
              </div>
            </div>
            <button className="landing-community__cta" onClick={() => { void startGoogleLogin() }}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t.landing.community.registerWithGoogle}
            </button>
          </div>
        </section>
      )}

      {/* ── Routes ── */}
      <section className="landing-routes">
        <div className="landing-routes__inner">
          <h2 className="landing-routes__heading">{t.landing.routes.heading}</h2>
          <p className="landing-routes__text">
            {t.landing.routes.text}
          </p>
          <div className="landing-routes__grid">
            {ROUTES.map(r => (
              <a key={r.id} href="/routes" className="landing-routes__card">
                <div className="landing-routes__card-accent" style={{ background: r.color }} />
                <span className="landing-routes__card-name">{r.name}</span>
                <span className="landing-routes__card-subtitle">{r.subtitle}</span>
                <span className="landing-routes__card-desc">{r.shortDesc}</span>
                <div className="landing-routes__card-stats">
                  <span>{r.totalKm} km</span>
                  <span>{estimateTime(r.totalKm)}</span>
                  <span>{r.pois.filter(p => p.type === 'manual').length} {t.landing.routes.stations}</span>
                </div>
              </a>
            ))}
          </div>
          <a href="/routes" className="landing-routes__link">
            {t.landing.routes.detailLink}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      {/* ── Stories ── */}
      <section className="landing-stories">
        <div className="landing-stories__inner">
          <h2 className="landing-stories__heading">{t.landing.stories.heading}</h2>
          <p className="landing-stories__text">
            {t.landing.stories.text}
          </p>
          <div className="landing-stories__grid">
            {STORIES.map(story => (
              <StoryCard key={story.id} story={story} compact actionHref={`/stories/${story.id}`} />
            ))}
          </div>
          <a href="/stories" className="landing-stories__link">
            {t.landing.stories.allLink}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      {/* ── Featured Species ── */}
      <section className="landing-featured">
        <div className="landing-featured__inner">
          <h2 className="landing-featured__heading">{t.landing.featured.heading}</h2>
          <div className="browse-grid">
            {featured.map(species => (
              <a key={species.storageId} href={`/s/${species.storageId}`} className="browse-card">
                <div className="browse-card__img-wrap">
                  <img src={thumbnailUrl(species.storageId)} alt={species.name} className="browse-card__img" loading="lazy" />
                </div>
                <div className="browse-card__info">
                  <span className="browse-card__name">{species.name}</span>
                  {species.latin && <span className="browse-card__latin">{species.latin}</span>}
                  <span className="browse-card__tag">{species.category}</span>
                </div>
              </a>
            ))}
          </div>
          <a href="/browse" className="landing-featured__all">
            {t.landing.featured.discoverLink(speciesCount)}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      {/* ── Footer ── */}
      <SiteFooter />

      <VerlaufRegistrationDialog
        open={showRegister}
        onClose={() => setShowRegister(false)}
        onGoogleRegister={() => { void startGoogleLogin() }}
      />

      <SurveyDialog
        open={showSurvey}
        onClose={() => { setShowSurvey(false); window.location.href = '/navigate-tile' }}
      />
    </div>
  )
}
